import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Phone, Image as ImageIcon, Upload, Loader2, RotateCcw } from 'lucide-react';
import {
  WhatsAppCloudTemplate, isMediaHeader, headerFormatOf, MEDIA_RULES, whatsappCloudService,
} from '@/services/integrations/whatsappCloudService';
import { renderTemplateBody, WA_BUBBLE_BG } from '@/utils/whatsappTemplate';

const PLACEHOLDER_RE = /\{\{\s*(\d+)\s*\}\}/g;

const filled = (s: string, values: Record<number, string>): string =>
  (s || '').replace(PLACEHOLDER_RE, (tok, n) => values[Number(n)]?.trim() || tok);

const hasPlaceholder = (s?: string | null): boolean => !!s && /\{\{\s*\d+\s*\}\}/.test(s);

export interface TemplateSendPayload {
  template_name: string;
  language: string;
  components?: any[];
  preview_text: string;
}

interface TemplateVariableFormProps {
  template: WhatsAppCloudTemplate;
  /** Cancels the send attempt — the template list stays visible beside this
   * panel (persistent split view), so this closes the whole picker rather
   * than navigating "back" to a grid that no longer exists. */
  onCancel: () => void;
  onSend: (payload: TemplateSendPayload) => void;
  sending: boolean;
}

/**
 * Fills a template's {{n}} body variables and builds the Meta `components`
 * payload — same shape journey_engine.build_template_components uses
 * (lowercase "body"/"text" keys), so a manual Inbox send and an automated
 * journey send hit Meta identically.
 */
const TemplateVariableForm: React.FC<TemplateVariableFormProps> = ({ template, onCancel, onSend, sending }) => {
  const paramIndexes = (template.parameters || []).map((p) => p.index).sort((a, b) => a - b);
  const [values, setValues] = useState<Record<number, string>>({});
  // Sample value per variable (Settings → WhatsApp). Shown as a hint and in the
  // preview until the agent types a real value — never sent on its own.
  const samples = useMemo(
    () => Object.fromEntries(
      (template.parameters || []).filter((p) => p.example).map((p) => [p.index, p.example as string]),
    ) as Record<number, string>,
    [template.parameters],
  );

  const mediaHeader = isMediaHeader(template);
  const headerFormat = headerFormatOf(template); // IMAGE | VIDEO | DOCUMENT
  const [headerMediaUrl, setHeaderMediaUrl] = useState(template.header_media_url || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaRule = MEDIA_RULES[headerFormat];

  /** One-off header for this send only — the template's default stays as it is. */
  const handleHeaderFile = async (file: File) => {
    setUploadError(null);
    if (mediaRule && file.size > mediaRule.maxMB * 1024 * 1024) {
      setUploadError(`File must be under ${mediaRule.maxMB}MB`);
      return;
    }
    setUploading(true);
    try {
      setHeaderMediaUrl(await whatsappCloudService.uploadHeaderForSend(file, headerFormat));
    } catch (e) {
      setUploadError((axios.isAxiosError(e) && e.response?.data?.message) || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // URL buttons carrying a {{1}} — the only dynamic-button shape Meta allows.
  const dynamicUrlButtons = useMemo(
    () => (template.buttons || [])
      .map((b, index) => ({ ...b, index }))
      .filter((b) => b.type === 'URL' && hasPlaceholder(b.url)),
    [template.buttons],
  );
  const [buttonValues, setButtonValues] = useState<Record<number, string>>({});
  const buttonSamples = template.button_examples || {};
  const hasSamples = Object.keys(samples).length > 0
    || dynamicUrlButtons.some((b) => buttonSamples[String(b.index)]);

  /** Copy samples into the empty fields only — never overwrite what the agent typed. */
  const fillWithSamples = () => {
    setValues((v) => {
      const next = { ...v };
      paramIndexes.forEach((i) => { if (!(next[i] || '').trim() && samples[i]) next[i] = samples[i]; });
      return next;
    });
    setButtonValues((v) => {
      const next = { ...v };
      dynamicUrlButtons.forEach((b) => {
        const ex = buttonSamples[String(b.index)];
        if (!(next[b.index] || '').trim() && ex) next[b.index] = ex;
      });
      return next;
    });
  };

  const allFilled = paramIndexes.every((i) => (values[i] || '').trim())
    && (!mediaHeader || headerMediaUrl.trim())
    && dynamicUrlButtons.every((b) => (buttonValues[b.index] || '').trim());
  // What gets sent — real values only.
  const sentBody = useMemo(() => filled(template.body, values), [template.body, values]);
  // What the preview shows — samples stand in for fields not typed yet.
  const previewBody = useMemo(() => {
    const merged: Record<number, string> = { ...samples };
    Object.entries(values).forEach(([k, v]) => { if (v.trim()) merged[Number(k)] = v; });
    return filled(template.body, merged);
  }, [template.body, values, samples]);
  const previewButtons = useMemo(
    () => (template.buttons || []).map((b, index) => (
      b.type === 'URL' && hasPlaceholder(b.url)
        ? { ...b, url: filled(b.url || '', { 1: buttonValues[index] || buttonSamples[String(index)] || '' }) }
        : b
    )),
    [template.buttons, buttonValues, buttonSamples],
  );

  const handleSend = () => {
    const components: any[] = [];

    if (mediaHeader && headerMediaUrl.trim()) {
      const kind = headerFormat.toLowerCase();
      const media: Record<string, any> = { link: headerMediaUrl.trim() };
      if (headerFormat === 'DOCUMENT') media.filename = headerMediaUrl.trim().split('/').pop();
      components.push({ type: 'header', parameters: [{ type: kind, [kind]: media }] });
    }

    if (paramIndexes.length) {
      components.push({
        type: 'body',
        parameters: paramIndexes.map((i) => ({ type: 'text', text: values[i].trim() })),
      });
    }

    dynamicUrlButtons.forEach((b) => {
      components.push({
        type: 'button', sub_type: 'url', index: String(b.index),
        parameters: [{ type: 'text', text: (buttonValues[b.index] || '').trim() }],
      });
    });

    onSend({
      template_name: template.element_name,
      language: template.language,
      components: components.length ? components : undefined,
      preview_text: sentBody,
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div>
          <div className="text-[15px] font-bold text-gray-900 font-mono">{template.element_name}</div>
          <div className="text-[11.5px] text-gray-500 mt-0.5">{template.category} · {template.language}</div>
        </div>

        {mediaHeader && (
          <div>
            <label className="text-[11.5px] text-gray-500 mb-1 block">
              Header {headerFormat.toLowerCase()}
              {template.header_media_url && headerMediaUrl === template.header_media_url && (
                <span className="text-gray-400"> · template default</span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                value={headerMediaUrl}
                onChange={(e) => setHeaderMediaUrl(e.target.value)}
                placeholder={`Upload, or paste https://…/${headerFormat.toLowerCase()}.${headerFormat === 'IMAGE' ? 'jpg' : headerFormat === 'VIDEO' ? 'mp4' : 'pdf'}`}
                className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
              />
              <input
                ref={fileRef}
                type="file"
                accept={mediaRule?.accept}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = ''; // allow re-picking the same file
                  if (f) handleHeaderFile(f);
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              {template.header_media_url && headerMediaUrl !== template.header_media_url && (
                <button
                  type="button"
                  onClick={() => { setHeaderMediaUrl(template.header_media_url || ''); setUploadError(null); }}
                  title="Back to the template's default"
                  className="px-2.5 py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 flex-shrink-0"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
            {mediaRule && (
              <div className="text-[11px] text-gray-400 mt-1">
                {mediaRule.accept.split(',').map((m) => m.split('/')[1].toUpperCase()).join(', ')} · up to {mediaRule.maxMB}MB · used for this message only
              </div>
            )}
            {uploadError && <div className="text-[11px] text-red-600 mt-1">{uploadError}</div>}
            {!headerMediaUrl.trim() && (
              <div className="text-[11px] text-amber-600 mt-1">Required — this template's header needs a {headerFormat.toLowerCase()} per send.</div>
            )}
          </div>
        )}

        {paramIndexes.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-gray-500">Fill in the variables</div>
              {hasSamples && (
                <button
                  type="button"
                  onClick={fillWithSamples}
                  className="text-[11.5px] font-medium text-brandColor hover:underline"
                >
                  Fill with samples
                </button>
              )}
            </div>
            {paramIndexes.map((i) => (
              <div key={i}>
                <label className="text-[11.5px] text-gray-500 mb-1 block">
                  {`{{${i}}}`}
                  {samples[i] && <span className="text-gray-400"> · sample: {samples[i]}</span>}
                </label>
                <input
                  value={values[i] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [i]: e.target.value }))}
                  placeholder={samples[i] ? `e.g. ${samples[i]}` : 'Value'}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                />
              </div>
            ))}
          </div>
        )}

        {dynamicUrlButtons.length > 0 && (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-gray-500">Fill in the button link{dynamicUrlButtons.length > 1 ? 's' : ''}</div>
            {dynamicUrlButtons.map((b) => (
              <div key={b.index}>
                <label className="text-[11.5px] text-gray-500 mb-1 block">
                  “{b.text}” — value for {`{{1}}`} in <span className="font-mono">{b.url}</span>
                </label>
                <input
                  value={buttonValues[b.index] || ''}
                  onChange={(e) => setButtonValues((v) => ({ ...v, [b.index]: e.target.value }))}
                  placeholder={`e.g. ${buttonSamples[String(b.index)] || 'ashwagandha-probiotic-supplements'}`}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                />
              </div>
            ))}
          </div>
        )}

        <div>
          <div className="text-xs font-medium text-gray-500 mb-2">Preview</div>
          <div className="rounded-xl p-3" style={{ background: WA_BUBBLE_BG }}>
            <div className="bg-white rounded-lg rounded-tl-none shadow-sm overflow-hidden max-w-[90%]">
              {mediaHeader && (
                headerFormat === 'IMAGE' && headerMediaUrl.trim() ? (
                  <img src={headerMediaUrl.trim()} alt="" className="w-full max-h-48 object-cover" />
                ) : (
                  <div className="flex items-center gap-2 px-3 pt-3 text-[11.5px] text-gray-500">
                    <ImageIcon className="w-3.5 h-3.5" /> {headerFormat.toLowerCase()} header
                  </div>
                )
              )}
              <div className="p-3">
                {!mediaHeader && template.header && (
                  <div className="text-sm font-semibold text-gray-900 mb-1">{template.header}</div>
                )}
                <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {renderTemplateBody(previewBody)}
                </div>
                {template.footer && <div className="text-[11px] text-gray-400 mt-2">{template.footer}</div>}
              </div>
            </div>
            {previewButtons.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {previewButtons.map((b, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-sm py-2 text-center text-sm text-[#00a5f4] font-medium flex items-center justify-center gap-1.5">
                    {b.type === 'URL' && <ExternalLink className="w-3.5 h-3.5" />}
                    {b.type === 'PHONE_NUMBER' && <Phone className="w-3.5 h-3.5" />}
                    {b.text || b.type}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 p-4 flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={!allFilled || sending}
          className="px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send template'}
        </button>
      </div>
    </div>
  );
};

export default TemplateVariableForm;
